import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  Command,
  CommandDialog,
  CommandDialogPopup,
  CommandInput,
  CommandList,
  CommandPanel,
  Drawer,
  DrawerPopup,
  DrawerTitle,
  Kbd,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import { FeedbackDialog } from "#app/components/navigation/feedback-dialog";
import {
  useGlobalSearchOpen,
  useGlobalSearchActions,
} from "./global-search-context";
import { useGlobalSearchContent } from "./use-global-search-content";
import { GlobalSearchFooter } from "./global-search-footer";

// Generous scroll-end breathing room below the last result: the list scrolls
// 48px past its final row instead of pinning it against the panel edge.
// (CommandList's own gutter is 8px — tracked library gap.)
const LIST_BOTTOM_GUTTER = "not-empty:pb-12";

/**
 * Global search as a drawer on mobile and a top-centered command dialog on
 * desktop. The compact desktop header bar is a trigger for this surface. Both
 * presentations share the result and command rendering through
 * `useGlobalSearchContent`.
 */
export function GlobalSearchDialog() {
  const open = useGlobalSearchOpen();
  const { closeSearch } = useGlobalSearchActions();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) closeSearch();
    },
    [closeSearch],
  );

  // Focus the input only once the drawer finishes its open animation. Focusing
  // mid-animation opens the virtual keyboard, which resizes the visual viewport
  // and desyncs the drawer's translate math.
  const onOpenChangeComplete = useCallback((isOpen: boolean) => {
    if (isOpen) inputRef.current?.focus();
  }, []);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const {
    query,
    setQuery,
    itemsContent,
    scopeBar,
    footerContext,
    footerEnterVerb,
    onItemHighlighted,
    onInputKeyDown,
    listRef,
    feedbackOpen,
    setFeedbackOpen,
  } = useGlobalSearchContent({ onClose: closeSearch, focusInput });

  // Every entry starts clean regardless of whether the prior close came from
  // Escape, the backdrop, a result, or a command.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open, setQuery]);

  const command = (
    <Command
      // Desktop keeps the first row highlighted (the library default), so the
      // footer's "hit Enter to open" acts on it without an Arrow Down first.
      // Touch has no Enter legend, and a painted highlight there reads as a
      // pressed row.
      autoHighlight={isMobile ? false : "always"}
      mode="none"
      onItemHighlighted={onItemHighlighted}
      onValueChange={setQuery}
      value={query}
    >
      {/* The input line. Desktop: the borderless ⌘K input fills the popup's
          header zone. Phone: the sheet has no title — the field IS the
          header — so it wears the bordered `inset` treatment every other
          phone sheet's search band uses (Views, Filters, the widget
          gallery), with the search glyph saying what the missing title
          said. It sits on the sheet's 24px line: 16px here + the inset
          wrapper's 8px. */}
      <Box
        align="center"
        direction="row"
        paddingTop={isMobile ? "md" : undefined}
        paddingX={isMobile ? "md" : undefined}
      >
        <Box grow minWidth={0}>
          {/* 16px/600 at every breakpoint — the input's stock sm:text-sm
              would drop the dialog's query text to 14px on desktop. The
              phone field keeps a plain weight: it is one search band among
              the app's sheets, not the ⌘K palette. */}
          <CommandInput
            ref={inputRef}
            autoFocus={!isMobile}
            className={isMobile ? undefined : "font-semibold sm:text-base"}
            inset={isMobile}
            onKeyDown={onInputKeyDown}
            placeholder="Search anything or run a command..."
            // Circle-× clear at the input's end while there's a query (Base
            // UI hides it when empty). Esc is wired in onInputKeyDown
            // (clear-first, close on the second press).
            showClear
            clearTooltip={
              <Box direction="row" align="center" gap="sm">
                <Text size="xs">Clear</Text>
                <Kbd>Esc</Kbd>
              </Box>
            }
            startAddon={isMobile ? undefined : null}
          />
        </Box>
      </Box>
      {/* Desktop gets the coss command grammar: the input sits on the popup
          surface, results live in the inset rounded CommandPanel, and the
          CommandFooter closes the popup below it. The scope chips sit INSIDE
          the panel but above the scroll — pinned, so the active scopes stay
          readable while results scroll beneath them. The mobile drawer keeps
          a flat list — a bordered inset panel is dialog grammar, not drawer
          grammar — and draws the panel's seam as ONE hairline under the
          pinned chips, so the fixed chrome (field, scopes) and the scrolling
          results read as two regions the way they do on desktop. Chips and
          rows both land on the sheet's 24px line: the chip band's own 16px
          and the list's 8px gutter + 8px row inset each get 8px more here.
          grow + min-height:0 hands the library ScrollArea inside CommandList
          a definite height either way, so the list scrolls instead of
          pushing the surface. */}
      {isMobile ? (
        <Box direction="column" grow minHeight={0}>
          <Box border="bottom" paddingX="sm" shrink={false}>
            {scopeBar}
          </Box>
          <Box direction="column" grow minHeight={0} paddingX="sm">
            <CommandList className={LIST_BOTTOM_GUTTER} ref={listRef}>
              {itemsContent}
            </CommandList>
          </Box>
        </Box>
      ) : (
        <CommandPanel>
          {scopeBar}
          <Box direction="column" grow minHeight={0}>
            <CommandList className={LIST_BOTTOM_GUTTER} ref={listRef}>
              {itemsContent}
            </CommandList>
          </Box>
        </CommandPanel>
      )}
    </Command>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          onOpenChange={onOpenChange}
          onOpenChangeComplete={onOpenChangeComplete}
          open={open}
          size="full"
        >
          <DrawerPopup showBar>
            {/* No painted title: a "Search" heading over a field whose
                placeholder already says search was a second title (the
                project sheet dropped its repeated title the same way). The
                sheet keeps its accessible name; the field is the header. */}
            <DrawerTitle visuallyHidden>Search</DrawerTitle>
            {command}
          </DrawerPopup>
        </Drawer>
      ) : (
        <CommandDialog onOpenChange={onOpenChange} open={open}>
          {/* Keep the former expanded surface's 800px measure. The COSS
              viewport centers it horizontally near the top of the screen. */}
          <CommandDialogPopup
            aria-label="Global search"
            className="max-w-[800px]"
            fixedHeight={540}
          >
            {command}
            <GlobalSearchFooter
              context={footerContext}
              enterVerb={footerEnterVerb}
            />
          </CommandDialogPopup>
        </CommandDialog>
      )}

      {feedbackOpen && (
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      )}
    </>
  );
}
